import { useEffect, useMemo, useState } from 'react';

import { GrowthUnifiedProfile } from '@/libraries/growth-unified-profile-client/unified-profile.schema';
import { useGupsStatus } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups';

import { useLoggedOut } from '../../../../components/user-context/helpers/cohorts';
import { useUserContext } from '../../../../components/user-context/user-context-provider';
import {
	useGeoLocation,
	type GeoRegion,
	getRegionFromCountryCode,
} from '../../../../utils/useGeoCurrency';
import { useWAEContext } from '../../../utils/wae-context-provider';

import { getSoftwarePageProductRecommendation } from './software-page-product-recommendation';

export enum UserSegmentTypes {
	LOGIN_STATUS = 'login-status',
	GEOLOCATION = 'geolocation',
	JOB_FUNCTION = 'job-function',
	DOMAIN_TYPE = 'domain-type',
	OWNED_PRODUCTS = 'owned-products',
	PRODUCT_USED_EVER = 'product-used-ever',
	CLUSTER_TAG = 'cluster-tag',
	SUB_ANCHOR = 'sub-anchor',
	CHANNEL = 'channel',
	UTM_CONTENT = 'utm-content',
	FIRST_RECOMMENDATION = 'first-recommendation',
	SECOND_RECOMMENDATION = 'second-recommendation',
	THIRD_RECOMMENDATION = 'third-recommendation',
	FOURTH_RECOMMENDATION = 'fourth-recommendation',
	COLLECTION_OPTIONS = 'collection-options',
}

export type SegmentStatus = 'idle' | 'fetching' | 'complete' | 'error';

export interface UserSegmentResponse {
	data?: UserSegmentValues;
	error?: unknown;
	segmentFetchErrors?: UserSegmentTypes[];
	statuses: Record<UserSegmentTypes, SegmentStatus>;
}

export type ProductRecommendationOptions = 'jira' | 'confluence' | 'jsm' | 'loom' | 'jpd' | 'rovo';
export interface UserSegmentValues {
	[UserSegmentTypes.LOGIN_STATUS]?: 'loggedIn' | 'loggedOut';
	[UserSegmentTypes.GEOLOCATION]?: GeoRegion;
	[UserSegmentTypes.JOB_FUNCTION]?: string;
	[UserSegmentTypes.DOMAIN_TYPE]?: string;
	[UserSegmentTypes.OWNED_PRODUCTS]?: string[];
	[UserSegmentTypes.PRODUCT_USED_EVER]?: string[];
	[UserSegmentTypes.CLUSTER_TAG]?: string;
	[UserSegmentTypes.SUB_ANCHOR]?: string;
	[UserSegmentTypes.CHANNEL]?: string;
	[UserSegmentTypes.UTM_CONTENT]?: string;
	[UserSegmentTypes.FIRST_RECOMMENDATION]?: ProductRecommendationOptions;
	[UserSegmentTypes.SECOND_RECOMMENDATION]?: ProductRecommendationOptions;
	[UserSegmentTypes.THIRD_RECOMMENDATION]?: ProductRecommendationOptions;
	[UserSegmentTypes.FOURTH_RECOMMENDATION]?: ProductRecommendationOptions;
	/**
	 * Collections the user is eligible for (e.g. "TWC").
	 * Stubbed as undefined — downstream data source not yet available.
	 */
	[UserSegmentTypes.COLLECTION_OPTIONS]?: string[];
}

const extractProductNames = (
	sites?: Array<{
		products?: Array<{ productName?: string; productKey?: string }>;
	}>,
) => {
	if (!sites) return [];

	return sites.flatMap(
		(site) =>
			site.products
				?.map((product) => product.productName || product.productKey || '')
				.filter(Boolean) || [],
	);
};

const PRODUCT_USED_EVER_KEY_ALIASES: Record<string, string> = {
	'jira-software.ondemand': 'jira-software',
	'confluence.ondemand': 'confluence',
	'jira-servicedesk.ondemand': 'jira-servicedesk',
	'jira-product-discovery.ondemand': 'jira-product-discovery',
	'loom.ondemand': 'loom',
};

const PRODUCT_USED_EVER_NAME_ALIASES: Record<string, string> = {
	jira: 'jira-software',
	confluence: 'confluence',
	'jira service management': 'jira-servicedesk',
	loom: 'loom',
	'jira product discovery': 'jira-product-discovery',
	rovo: 'rovo',
};

const normalizeProductUsedEver = (productKey?: string, productName?: string) => {
	const normalizedProductKey = productKey?.toLowerCase();
	if (normalizedProductKey) {
		return PRODUCT_USED_EVER_KEY_ALIASES[normalizedProductKey] ?? normalizedProductKey;
	}

	return productName ? PRODUCT_USED_EVER_NAME_ALIASES[productName.toLowerCase()] : undefined;
};

const extractProductsUsedEver = (
	sites?: Array<{
		products?: Array<{
			productName?: string;
			productKey?: string;
			everActive?: boolean;
		}>;
	}>,
): string[] => {
	if (!sites) return [];

	const values = sites.flatMap((site) =>
		(site.products ?? []).flatMap((product): string[] => {
			const normalizedProduct = normalizeProductUsedEver(product.productKey, product.productName);
			if (!normalizedProduct || product.everActive !== true) return [];
			return [normalizedProduct];
		}),
	);

	return [...new Set(values)];
};

// temporary implementation for testing purposes
export const useUserSegments = (): UserSegmentResponse => {
	const { personalizationValues } = useWAEContext();
	const { loggedOutProfile, loggedInProfile, profile: userContextProfile } = useUserContext();
	const [profileStatus, setProfileStatus] = useState<SegmentStatus>(
		loggedInProfile ? 'complete' : 'idle',
	);
	const [profile, setProfile] = useState<Partial<GrowthUnifiedProfile> | undefined>(
		loggedInProfile,
	);
	const [profileError, setProfileError] = useState<unknown>(null);
	const [segmentFetchErrors, setSegmentFetchErrors] = useState<UserSegmentTypes[]>([]);

	// works on SSR and client
	const isLoggedOut = useLoggedOut();

	// Memoize the client to prevent recreation on every render
	const { getFullProfileCallback } = useGupsStatus();
	const productCatalogLocationBaseUrl =
		typeof window !== 'undefined'
			? (
					window as Window & {
						PRODUCT_CATALOG_LOCATION_BASE_URL?: string;
					}
				).PRODUCT_CATALOG_LOCATION_BASE_URL
			: process.env['PRODUCT_CATALOG_LOCATION_BASE_URL'];
	const { countryCode, status: locationStatus } = useGeoLocation(
		productCatalogLocationBaseUrl ?? '',
	);
	useEffect(() => {
		if (loggedInProfile) {
			setProfile(loggedInProfile);
			setSegmentFetchErrors([]);
			setProfileStatus('complete');
			return;
		}

		let isCancelled = false;
		const getFullProfile = async () => {
			setProfileStatus('fetching');
			const { data, error } = await getFullProfileCallback();
			// Add this to prevent Race Condition when the component is unmounted before the request is complete
			if (isCancelled) return;
			setProfileError(error);
			setProfile(data);
			setSegmentFetchErrors(
				error
					? [
							UserSegmentTypes.JOB_FUNCTION,
							UserSegmentTypes.DOMAIN_TYPE,
							UserSegmentTypes.PRODUCT_USED_EVER,
						]
					: [],
			);
			setProfileStatus(error ? 'error' : 'complete');
		};

		getFullProfile();

		return () => {
			isCancelled = true;
		};
	}, [getFullProfileCallback, loggedInProfile]);

	const userContextSites = userContextProfile.attributes?.existingSites;
	const hasUserContextProducts =
		!userContextProfile.requestInProgress && userContextSites !== undefined;

	// Memoize the personalization values to prevent unnecessary recalculations
	return useMemo(() => {
		// Collect products from both logged-in and logged-out profiles
		const loggedInProducts = extractProductNames(profile?.sites);
		const userContextProducts = extractProductNames(userContextSites);
		const loggedOutProducts = extractProductNames(loggedOutProfile?.sites);
		const allProducts = [...loggedInProducts, ...userContextProducts, ...loggedOutProducts];
		const uniqueProducts = [...new Set(allProducts)];
		const productsUsedEver = extractProductsUsedEver(profile?.sites);
		const productRecommendation = getSoftwarePageProductRecommendation({
			jobFunction: profile?.userProfile?.jobFunction,
			loggedInProducts: loggedInProducts,
		});
		const geoRegion = getRegionFromCountryCode(countryCode);

		// Build the user segment values
		const userSegmentData: UserSegmentValues = {
			...personalizationValues,
			[UserSegmentTypes.LOGIN_STATUS]: isLoggedOut ? 'loggedOut' : 'loggedIn',
			[UserSegmentTypes.GEOLOCATION]: locationStatus === 'complete' ? geoRegion : undefined,
			[UserSegmentTypes.JOB_FUNCTION]: profile?.userProfile?.jobFunction,
			[UserSegmentTypes.DOMAIN_TYPE]: profile?.userProfile?.domainType,
			[UserSegmentTypes.OWNED_PRODUCTS]: uniqueProducts,
			[UserSegmentTypes.PRODUCT_USED_EVER]: productsUsedEver,
			[UserSegmentTypes.FIRST_RECOMMENDATION]:
				productRecommendation.FIRST_Recommendation as ProductRecommendationOptions,
			[UserSegmentTypes.SECOND_RECOMMENDATION]:
				productRecommendation.SECOND_Recommendation as ProductRecommendationOptions,
			[UserSegmentTypes.THIRD_RECOMMENDATION]:
				productRecommendation.THIRD_Recommendation as ProductRecommendationOptions,
			[UserSegmentTypes.FOURTH_RECOMMENDATION]:
				productRecommendation.FOURTH_Recommendation as ProductRecommendationOptions,
			// Stubbed: downstream data source for collection eligibility not yet available.
			// Will be populated (e.g. with "TWC") once the upstream API is ready.
			[UserSegmentTypes.COLLECTION_OPTIONS]: undefined,
		};

		const ownedProductsStatus =
			hasUserContextProducts || loggedInProfile || loggedOutProfile ? 'complete' : profileStatus;
		const statuses: Record<UserSegmentTypes, SegmentStatus> = {
			// always available on SSR, synchronously
			[UserSegmentTypes.LOGIN_STATUS]: 'complete',
			[UserSegmentTypes.CLUSTER_TAG]: 'complete',
			[UserSegmentTypes.SUB_ANCHOR]: 'complete',
			[UserSegmentTypes.CHANNEL]: 'complete',
			[UserSegmentTypes.UTM_CONTENT]: 'complete',
			// depends on the location check
			[UserSegmentTypes.GEOLOCATION]: locationStatus as SegmentStatus,
			// depends on the profile
			[UserSegmentTypes.JOB_FUNCTION]: profileStatus,
			[UserSegmentTypes.DOMAIN_TYPE]: profileStatus,
			[UserSegmentTypes.OWNED_PRODUCTS]: ownedProductsStatus,
			[UserSegmentTypes.PRODUCT_USED_EVER]: profileStatus,
			[UserSegmentTypes.FIRST_RECOMMENDATION]: profileStatus,
			[UserSegmentTypes.SECOND_RECOMMENDATION]: profileStatus,
			[UserSegmentTypes.THIRD_RECOMMENDATION]: profileStatus,
			[UserSegmentTypes.FOURTH_RECOMMENDATION]: profileStatus,
			// Stubbed: always complete since it's not fetched from downstream yet
			[UserSegmentTypes.COLLECTION_OPTIONS]: 'complete',
		};

		return {
			data: userSegmentData,
			error: profileError,
			segmentFetchErrors,
			statuses,
		};
	}, [
		profile?.sites,
		userContextSites,
		profile?.userProfile?.domainType,
		profile?.userProfile?.jobFunction,
		loggedOutProfile?.sites,
		profileError,
		segmentFetchErrors,
		countryCode,
		profileStatus,
		locationStatus,
		loggedInProfile,
		personalizationValues,
		isLoggedOut,
	]);
};
